import React, { useState, useMemo, useEffect } from 'react'
import { useSidebarContext } from '../scenes/global/sidebar/SidebarContext'
import { useSelector, useDispatch } from 'react-redux'
import { fetchTransactions } from '../store/slices/transactionSlice'
import { Calendar, BarChart3, PieChart, DollarSign, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Filter, Loader2 } from 'lucide-react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

const FinancialSummary = () => {
  const { theme, activeSociety } = useSidebarContext()
  const dispatch = useDispatch()
  const transactionsData = useSelector((state) => state.transactions)
  const { user } = useSelector((state) => state.auth)

  // Extract transactions from the correct state structure
  const transactions = useMemo(() => {
    return transactionsData?.transactions?.data?.transactions || []
  }, [transactionsData])

  const [expandedSections, setExpandedSections] = useState({
    daily: false,
    monthly: false,
    quarterly: false,
    yearly: false
  })

  // Date selection state
  const [selectedPeriod, setSelectedPeriod] = useState('yearly')
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [customDateRange, setCustomDateRange] = useState([null, null])
  const [isCustomRange, setIsCustomRange] = useState(false)

  // Generate year options (current year and previous 10 years)
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear()
    return Array.from({ length: 11 }, (_, i) => currentYear - i)
  }, [])

  // Fetch transactions based on selected date range
  useEffect(() => {
    if (activeSociety) {
      let startDate, endDate

      if (isCustomRange && customDateRange[0] && customDateRange[1]) {
        startDate = customDateRange[0].toISOString().split('T')[0]
        endDate = new Date(customDateRange[1].getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      } else {
        // For yearly selection
        startDate = `${selectedYear}-01-01`
        endDate = `${selectedYear + 1}-01-01`
      }

      dispatch(fetchTransactions({
        societyId: activeSociety._id,
        startDate,
        endDate,
        limit: 1000 // Fetch all transactions for the period
      }))
    }
  }, [activeSociety, selectedYear, customDateRange, isCustomRange, dispatch])

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const calculateSummary = (transactions, period) => {
    if (!transactions || transactions.length === 0) {
      return { income: 0, expense: 0, profit: 0, count: 0 }
    }

    let startDate, endDate

    if (isCustomRange && customDateRange[0] && customDateRange[1]) {
      startDate = customDateRange[0]
      endDate = customDateRange[1]
    } else {
      const now = new Date()
      const currentYear = selectedYear

      switch (period) {
        case 'daily':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
          endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
          break
        case 'monthly':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1)
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1)
          break
        case 'quarterly':
          const quarter = Math.floor(now.getMonth() / 3)
          startDate = new Date(now.getFullYear(), quarter * 3, 1)
          endDate = new Date(now.getFullYear(), (quarter + 1) * 3, 1)
          break
        case 'yearly':
          startDate = new Date(currentYear, 0, 1)
          endDate = new Date(currentYear + 1, 0, 1)
          break
        default:
          startDate = new Date(currentYear, 0, 1)
          endDate = new Date(currentYear + 1, 0, 1)
      }
    }

    const filteredTransactions = transactions.filter(transaction => {
      const transactionDate = new Date(transaction.transaction_date)
      return transactionDate >= startDate && transactionDate < endDate
    })

    const summary = filteredTransactions.reduce((acc, transaction) => {
      const amount = parseFloat(transaction.amount) || 0
      
      if (transaction.transaction_direction === 'Income') {
        acc.income += amount
      } else if (transaction.transaction_direction === 'Expense') {
        acc.expense += amount
      }
      
      acc.count++
      return acc
    }, { income: 0, expense: 0, profit: 0, count: 0 })

    summary.profit = summary.income - summary.expense
    return summary
  }

  const dailySummary = useMemo(() => calculateSummary(transactions, 'daily'), [transactions, selectedYear, customDateRange, isCustomRange])
  const monthlySummary = useMemo(() => calculateSummary(transactions, 'monthly'), [transactions, selectedYear, customDateRange, isCustomRange])
  const quarterlySummary = useMemo(() => calculateSummary(transactions, 'quarterly'), [transactions, selectedYear, customDateRange, isCustomRange])
  const yearlySummary = useMemo(() => calculateSummary(transactions, 'yearly'), [transactions, selectedYear, customDateRange, isCustomRange])

  const getPeriodLabel = (period) => {
    if (isCustomRange && customDateRange[0] && customDateRange[1]) {
      return `Custom Range (${customDateRange[0].toLocaleDateString()} - ${customDateRange[1].toLocaleDateString()})`
    }

    const now = new Date()
    switch (period) {
      case 'daily':
        return `Today (${now.toLocaleDateString()})`
      case 'monthly':
        return `${now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`
      case 'quarterly':
        const quarter = Math.floor(now.getMonth() / 3) + 1
        return `Q${quarter} ${now.getFullYear()}`
      case 'yearly':
        return `${selectedYear}`
      default:
        return `${selectedYear}`
    }
  }

  // Date selection handlers
  const handleYearChange = (year) => {
    setSelectedYear(year)
    setIsCustomRange(false)
    setCustomDateRange([null, null])
  }

  const handleCustomRangeChange = (dates) => {
    setCustomDateRange(dates)
    setIsCustomRange(true)
  }

  const resetToCurrentYear = () => {
    setSelectedYear(new Date().getFullYear())
    setIsCustomRange(false)
    setCustomDateRange([null, null])
  }

  const SummaryCard = ({ title, summary, period, icon: Icon, isExpanded, onToggle }) => (
    <div className={`p-3 rounded-lg border ${theme.tailwind.border10} ${theme.tailwind.bg5} min-h-[180px]`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <div className={`p-1.5 rounded-lg ${theme.tailwind.bg8}`}>
            <Icon className={`w-4 h-4 ${theme.tailwind.text9}`} />
          </div>
          <div>
            <h3 className={`text-sm font-semibold ${theme.tailwind.text9}`}>{title}</h3>
            <p className={`text-xs ${theme.tailwind.text10}`}>{getPeriodLabel(period)}</p>
          </div>
        </div>
        <button
          onClick={onToggle}
          className={`p-1 rounded-lg ${theme.tailwind.bg8} ${theme.tailwind.text9} hover:${theme.tailwind.bg9} transition-colors`}
        >
          {isExpanded ? '−' : '+'}
        </button>
      </div>

      {/* Amount Cards - Always Visible */}
      <div className="space-y-2 mb-2">
        {/* Income and Expense Row */}
        <div className="grid grid-cols-2 gap-2">
          <div className={`p-2 rounded-lg ${theme.tailwind.bg6} border ${theme.tailwind.border10} text-center min-h-[50px] flex flex-col justify-center`}>
            <div className="text-xs text-green-400 mb-1 font-medium">Income</div>
            <div className="font-bold text-green-400 text-xs leading-tight break-words">
              {formatCurrency(summary.income)}
            </div>
          </div>
          <div className={`p-2 rounded-lg ${theme.tailwind.bg6} border ${theme.tailwind.border10} text-center min-h-[50px] flex flex-col justify-center`}>
            <div className="text-xs text-red-400 mb-1 font-medium">Expense</div>
            <div className="font-bold text-red-400 text-xs leading-tight break-words">
              {formatCurrency(summary.expense)}
            </div>
          </div>
        </div>
        
        {/* Profit Row */}
        <div className={`p-2 rounded-lg ${theme.tailwind.bg6} border ${theme.tailwind.border10} text-center min-h-[50px] flex flex-col justify-center`}>
          <div className={`text-xs mb-1 font-medium ${summary.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {summary.profit >= 0 ? 'Profit' : 'Loss'}
          </div>
          <div className={`font-bold text-xs leading-tight break-words ${summary.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {formatCurrency(Math.abs(summary.profit))}
          </div>
        </div>
      </div>

      {/* Expandable Details */}
      {isExpanded && (
        <div className={`p-2 rounded-lg ${theme.tailwind.bg6} border ${theme.tailwind.border10}`}>
          <div className="text-xs text-gray-400 mb-1 font-medium">Transaction Details</div>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className={theme.tailwind.text10}>Total Transactions:</span>
              <span className={`${theme.tailwind.text9} font-semibold`}>{summary.count}</span>
            </div>
            <div className="flex justify-between">
              <span className={theme.tailwind.text10}>Average per Transaction:</span>
              <span className={`${theme.tailwind.text9} font-semibold`}>
                {summary.count > 0 ? formatCurrency((summary.income + summary.expense) / summary.count) : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )

  if (transactionsData.error) {
    return (
      <div className={`p-6 rounded-lg border ${theme.tailwind.border10} ${theme.tailwind.bg5}`}>
        <div className="text-center">
          <div className="text-red-400 mb-2">Error loading financial data</div>
          <div className={`text-sm ${theme.tailwind.text10}`}>{transactionsData.error}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4">
      {/* Date Selection Controls */}
      <div className={`mb-6 p-4 rounded-lg border ${theme.tailwind.border10} ${theme.tailwind.bg5}`}>
        <div className="flex items-center space-x-3 mb-3">
          <div className={`p-1.5 rounded-lg ${theme.tailwind.bg8}`}>
            <Filter className={`w-4 h-4 ${theme.tailwind.text9}`} />
          </div>
          <h2 className={`text-lg font-semibold ${theme.tailwind.text9}`}>
            Date Range Selection
          </h2>
          {transactionsData.loading && (
            <div className="flex items-center space-x-2">
              <Loader2 className="w-3 h-3 animate-spin text-blue-400" />
              <span className={`text-xs ${theme.tailwind.text10}`}>Loading...</span>
            </div>
          )}
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Year Selection */}
          <div>
            <label className={`block text-xs font-medium mb-1 ${theme.tailwind.text10}`}>
              Select Year
            </label>
            <select
              value={selectedYear}
              onChange={(e) => handleYearChange(parseInt(e.target.value))}
              className={`w-full px-2 py-1.5 text-sm rounded-lg border ${theme.tailwind.border10} ${theme.tailwind.bg6} ${theme.tailwind.text9} focus:outline-none focus:ring-2 focus:ring-blue-500`}
            >
              {yearOptions.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          
          {/* Custom Date Range */}
          <div>
            <label className={`block text-xs font-medium mb-1 ${theme.tailwind.text10}`}>
              Custom Date Range
            </label>
            <DatePicker
              selectsRange={true}
              startDate={customDateRange[0]}
              endDate={customDateRange[1]}
              onChange={handleCustomRangeChange}
              className={`w-full px-2 py-1.5 text-sm rounded-lg border ${theme.tailwind.border10} ${theme.tailwind.bg6} ${theme.tailwind.text9} focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholderText="Select date range"
              dateFormat="MMM dd, yyyy"
            />
          </div>
          
          {/* Quick Actions */}
          <div className="flex flex-col space-y-1">
            <label className={`block text-xs font-medium ${theme.tailwind.text10}`}>
              Quick Actions
            </label>
            <button
              onClick={resetToCurrentYear}
              className={`px-3 py-1.5 text-xs rounded-lg ${theme.tailwind.bg8} ${theme.tailwind.text9} hover:${theme.tailwind.bg9} transition-colors`}
            >
              Current Year
            </button>
          </div>
          
          {/* Selected Period Display */}
          <div>
            <label className={`block text-xs font-medium mb-1 ${theme.tailwind.text10}`}>
              Current Period
            </label>
            <div className={`px-2 py-1.5 text-sm rounded-lg ${theme.tailwind.bg6} ${theme.tailwind.text9} border ${theme.tailwind.border10}`}>
              {isCustomRange && customDateRange[0] && customDateRange[1] 
                ? `${customDateRange[0].toLocaleDateString()} - ${customDateRange[1].toLocaleDateString()}`
                : `${selectedYear}`
              }
            </div>
          </div>
        </div>
      </div>

      {/* Overall Performance */}
      <div className={`mb-6 p-4 rounded-lg border ${theme.tailwind.border10} ${theme.tailwind.bg5}`}>
        <h2 className={`text-lg font-semibold mb-3 ${theme.tailwind.text9}`}>
          Overall Performance
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className={`p-3 rounded-lg ${theme.tailwind.bg6} border ${theme.tailwind.border10}`}>
            <div className="flex items-center space-x-2 mb-1">
              <TrendingUp className="w-3 h-3 text-green-400" />
              <span className={`text-xs ${theme.tailwind.text10}`}>Total Income</span>
            </div>
            <div className="text-green-400 font-semibold text-base">
              {formatCurrency(yearlySummary.income)}
            </div>
          </div>
          <div className={`p-3 rounded-lg ${theme.tailwind.bg6} border ${theme.tailwind.border10}`}>
            <div className="flex items-center space-x-2 mb-1">
              <TrendingDown className="w-3 h-3 text-red-400" />
              <span className={`text-xs ${theme.tailwind.text10}`}>Total Expense</span>
            </div>
            <div className="text-red-400 font-semibold text-base">
              {formatCurrency(yearlySummary.expense)}
            </div>
          </div>
          <div className={`p-3 rounded-lg ${theme.tailwind.bg6} border ${theme.tailwind.border10}`}>
            <div className="flex items-center space-x-2 mb-1">
              {yearlySummary.profit >= 0 ? (
                <ArrowUpRight className="w-3 h-3 text-green-400" />
              ) : (
                <ArrowDownRight className="w-3 h-3 text-red-400" />
              )}
              <span className={`text-xs ${theme.tailwind.text10}`}>
                Net {yearlySummary.profit >= 0 ? 'Profit' : 'Loss'}
              </span>
            </div>
            <div className={`font-semibold text-base ${
              yearlySummary.profit >= 0 ? 'text-green-400' : 'text-red-400'
            }`}>
              {formatCurrency(Math.abs(yearlySummary.profit))}
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          title="Daily Summary"
          summary={dailySummary}
          period="daily"
          icon={Calendar}
          isExpanded={expandedSections.daily}
          onToggle={() => toggleSection('daily')}
        />
        <SummaryCard
          title="Monthly Summary"
          summary={monthlySummary}
          period="monthly"
          icon={BarChart3}
          isExpanded={expandedSections.monthly}
          onToggle={() => toggleSection('monthly')}
        />
        <SummaryCard
          title="Quarterly Summary"
          summary={quarterlySummary}
          period="quarterly"
          icon={PieChart}
          isExpanded={expandedSections.quarterly}
          onToggle={() => toggleSection('quarterly')}
        />
        <SummaryCard
          title="Yearly Summary"
          summary={yearlySummary}
          period="yearly"
          icon={DollarSign}
          isExpanded={expandedSections.yearly}
          onToggle={() => toggleSection('yearly')}
        />
      </div>
    </div>
  )
}

export default FinancialSummary 